import { BadgeIcons } from "@smartrr/shared/customerPortal/components/AquariusBadge";
import { ISODateString } from "@smartrr/shared/entities/ISODateString";
import {
  ISmartrrFinancialStatus,
  ISmartrrFulfillmentStatus,
  ISmartrrOrderFromDb,
  ISmartrrOrderStatus,
} from "@smartrr/shared/entities/Order";
import { IOrganization, VALID_DAYS } from "@smartrr/shared/entities/Organization";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ISchedule } from "@smartrr/shared/entities/Schedule";
import { getNextDates } from "@smartrr/shared/entities/Schedule/utils";
import { RRuleAnchor } from "@smartrr/shared/entities/Schedule/utils/getCorrectedRRule";
import { SubscriptionDeliveryMethod } from "@smartrr/shared/shopifyGraphQL/api";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import groupBy from 'lodash/groupBy';
import { RRule } from "rrule";

import { IModalBadgeProps } from "@customer-app/account/views/OperationAquarius/components/AquariusModal";
import { IPurchasable } from "@smartrr/shared/entities/Purchasable";

export const WEEKDAYS_LONG = [
  typedLocalize("defaults.dates.weekdays.Monday"),
  typedLocalize("defaults.dates.weekdays.Tuesday"),
  typedLocalize("defaults.dates.weekdays.Wednesday"),
  typedLocalize("defaults.dates.weekdays.Thursday"),
  typedLocalize("defaults.dates.weekdays.Friday"),
  typedLocalize("defaults.dates.weekdays.Saturday"),
  typedLocalize("defaults.dates.weekdays.Sunday"),
];

export const WEEKDAYS_SHORT = {
  en: ["S", "M", "T", "W", "T", "F", "S"],
  es: ["D", "L", "M", "M", "J", "V", "S"],
  fr: ["D", "L", "M", "M", "J", "V", "S"],
  it: ["D", "L", "M", "M", "G", "V", "S"],
};

export const MONTHS_LONG = [
  typedLocalize("defaults.dates.months.January"),
  typedLocalize("defaults.dates.months.February"),
  typedLocalize("defaults.dates.months.March"),
  typedLocalize("defaults.dates.months.April"),
  typedLocalize("defaults.dates.months.May"),
  typedLocalize("defaults.dates.months.June"),
  typedLocalize("defaults.dates.months.July"),
  typedLocalize("defaults.dates.months.August"),
  typedLocalize("defaults.dates.months.September"),
  typedLocalize("defaults.dates.months.October"),
  typedLocalize("defaults.dates.months.November"),
  typedLocalize("defaults.dates.months.December"),
];

export interface IOrderSchedule extends ISchedule {
  anchor: RRuleAnchor;
}

export interface IOrderStatus {
  fulfillmentStatus: ISmartrrFulfillmentStatus;
  financialStatus: ISmartrrFinancialStatus;
}

export const renderOrderIcon = (
  order: ISmartrrOrderFromDb,
  deliveryMethod?: SubscriptionDeliveryMethod
): BadgeIcons => {
  if (order.orderStatus === ISmartrrOrderStatus.CANCELLED || order.cancelledAt) {
    return "circleX";
  }
  if (deliveryMethod?.__typename === "SubscriptionDeliveryMethodPickup") {
    return "pickup";
  }
  if (
    order.fulfillmentStatus === ISmartrrFulfillmentStatus.FULFILLED ||
    order.fulfillmentStatus === ISmartrrFulfillmentStatus.PARTIALLY_SHIPPED
  ) {
    return "shippingBox";
  }
  if (order.financialStatus === ISmartrrFinancialStatus.PENDING_PAYMENT) {
    return "clock";
  }
  if (order.financialStatus === ISmartrrFinancialStatus.PARTIALLY_REFUNDED || ISmartrrFinancialStatus.REFUNDED) {
    return "circleMinus";
  }
  return "clock";
};

export const renderOrderStatus = (order: ISmartrrOrderFromDb, deliveryMethod?: SubscriptionDeliveryMethod) => {
  if (order.orderStatus === ISmartrrOrderStatus.CANCELLED || order.cancelledAt) {
    return typedLocalize("orderHistory.canceled");
  }
  if (deliveryMethod?.__typename === "SubscriptionDeliveryMethodPickup") {
    return typedLocalize("delivery.pickup");
  }
  if (order.fulfillmentStatus === ISmartrrFulfillmentStatus.FULFILLED) {
    return typedLocalize("orderHistory.shipped");
  }
  if (order.fulfillmentStatus === ISmartrrFulfillmentStatus.PARTIALLY_SHIPPED) {
    return typedLocalize("orderHistory.partiallyShipped");
  }
  if (order.financialStatus === ISmartrrFinancialStatus.PENDING_PAYMENT) {
    return typedLocalize("orderHistory.processing");
  }
  if (order.financialStatus === ISmartrrFinancialStatus.REFUNDED) {
    return typedLocalize("orderHistory.refunded");
  }
  if (order.financialStatus === ISmartrrFinancialStatus.PARTIALLY_REFUNDED) {
    return typedLocalize("orderHistory.partiallyRefunded");
  }
  return typedLocalize("orderHistory.processing");
};

export const dateFromDay = (year: number, day: number) => {
  const date = new Date(year, 0);
  return new Date(date.setDate(day));
};

export const renderSequenceNumber = (day: number) => {
  const stringDay = day.toString().trim();
  const lastDigitOfDayToString = stringDay[stringDay.length - 1];
  const penultimateDigitOfDayToString = stringDay?.[stringDay.length - 2];

  if (penultimateDigitOfDayToString === "1") {
    return `${stringDay}th`;
  }

  switch (+lastDigitOfDayToString) {
    case 1: {
      return `${stringDay}st`;
    }
    case 2: {
      return `${stringDay}nd`;
    }
    case 3: {
      return `${stringDay}rd`;
    }
    default: {
      return `${stringDay}th`;
    }
  }
};

export const renderCarrierLink = (fallbackEmail?: string, carrier?: string) => {
  if (carrier === undefined && !fallbackEmail) {
    return "";
  } else if (carrier === undefined) {
    return fallbackEmail ? `mailto:${fallbackEmail}` : "";
  }

  switch (true) {
    case carrier.includes("USPS"): {
      return "https://www.usps.com/help/contact-us.htm";
    }
    case carrier.includes("UPS"): {
      return "https://www.ups.com/us/en/support/contact-us.page";
    }
    case carrier.includes("FedEx"): {
      return "https://www.fedex.com/en-us/customer-support/contact-us.html";
    }
    case carrier.includes("DHL"): {
      return "https://www.dhl.com/global-en/home/contact-us.html";
    }
    default: {
      return `mailto:${fallbackEmail}`;
    }
  }
};

export const renderOrderFiniteState = (subscription: IPurchaseState, organization: IOrganization) => {
  const { maxCycles = 0, minCycles = 0, totalBillingCycles = 0 } = subscription.schedule;
  const { setup } = organization;
  const orgBillingDays = setup?.billingDays ?? VALID_DAYS;
  const rrule = RRule.fromString(subscription.schedule.deliveryRRuleString);
  const nextDateForMax = getNextDates(rrule, maxCycles || 0, orgBillingDays).pop();
  const nextDateForMin = getNextDates(rrule, minCycles || 0, orgBillingDays).pop();

  if (maxCycles) {
    return typedLocalize("orderHistory.finiteMaxCycles", {
      date: typedLocalize("utils.dates.toViewDateLong", {
        date: ISODateString.fromString(nextDateForMax).toJSDate(),
      }),
    });
  }

  if (minCycles && minCycles > totalBillingCycles) {
    return typedLocalize("orderHistory.finiteMinCycles", {
      date: typedLocalize("utils.dates.toViewDateLong", {
        date: ISODateString.fromString(nextDateForMin).toJSDate(),
      }),
    });
  }

  return;
};

export const getModalBadges = (order: ISmartrrOrderFromDb) => {
  const badges: IModalBadgeProps[] = [];
  const subscription = order.sts.find(st => st.shopifyId === order.items[0].shopifySubscriptionId);
  const itemsGroupedBySubscriptionsId = groupBy(
    order.items,
    ({ shopifySubscriptionId }) => shopifySubscriptionId
  );

  if (
    order.sts.reduce((acc, item) => (acc += item?.gifts?.length ?? 0), 0) ===
    Object.keys(itemsGroupedBySubscriptionsId).length
  ) {
    badges.push({ icon: "gift", label: typedLocalize("orderHistory.gifted") });
  } else if (order.sts.filter(item => !!item.gifts?.length).length) {
    badges.push({ icon: "gift", label: typedLocalize("orderHistory.partiallyGifted") });
  }
  badges.push({
    icon: renderOrderIcon(order, subscription?.deliveryMethod),
    label: renderOrderStatus(order, subscription?.deliveryMethod),
  });
  return badges;
};

export const renderOrderAnchorDatesState = (anchor: RRuleAnchor) => {
  if (anchor.byweekday != undefined) {
    return typedLocalize("orderHistory.anchorWeek", {
      anchor: Array.isArray(anchor.byweekday) ? WEEKDAYS_LONG[+anchor.byweekday[0]] : anchor.byweekday,
    });
  }

  if (anchor.bymonthday != undefined) {
    return typedLocalize("orderHistory.anchorMonth", {
      anchor: renderSequenceNumber(Array.isArray(anchor.bymonthday) ? anchor.bymonthday[0] : anchor.bymonthday),
    });
  }

  if (anchor.bymonth != undefined && anchor.bymonthday != undefined && Array.isArray(anchor.bymonth)) {
    return typedLocalize("orderHistory.anchorYear", {
      anchorMonth: MONTHS_LONG[anchor.bymonth[0]],
      anchorDay: renderSequenceNumber(anchor.bymonthday[0]),
    });
  }

  if (anchor.byyearday != undefined && Array.isArray(anchor.byyearday)) {
    return anchor.byyearday
      .map((yearDay: number, index: number) => {
        const dateFromAnchorYear = dateFromDay(new Date().getFullYear(), yearDay);
        return index === 0
          ? typedLocalize("orderHistory.anchorYear", {
              anchorMonth: MONTHS_LONG[dateFromAnchorYear.getMonth()],
              anchorDay: renderSequenceNumber(dateFromAnchorYear.getDate()),
            })
          : `${MONTHS_LONG[dateFromAnchorYear.getMonth()]}, ${renderSequenceNumber(
              dateFromAnchorYear.getDate()
            )}`;
      })
      .join("; ");
  }
};

export const getProductNameFromVariantId = (shopifyId: string | undefined, purchasables: IPurchasable[]) => {
  return purchasables.find(p => p.vnts?.find(v => v.shopifyId === shopifyId))?.purchasableName;
};
